'use client'

import { createContext, useContext, useEffect, useState } from 'react'
import type { CreditsContextType } from '@/types/credits'

const CreditsContext = createContext<CreditsContextType | undefined>(undefined)

const CREDITS_STORAGE_KEY = 'insightthread_credits'
const CREDITS_DATE_KEY = 'insightthread_credits_date'
const FREE_CREDITS_LIMIT = 5

export function CreditsProvider({ children }: { children: React.ReactNode }) {
    const [credits, setCredits] = useState<number>(0)
    const [isExhausted, setIsExhausted] = useState(false)

    useEffect(() => {
        // Get stored credits and date
        const storedCredits = localStorage.getItem(CREDITS_STORAGE_KEY)
        const storedDate = localStorage.getItem(CREDITS_DATE_KEY)
        const today = new Date().toDateString()

        // Reset credits if it's a new day
        if (storedDate !== today) {
            localStorage.setItem(CREDITS_STORAGE_KEY, FREE_CREDITS_LIMIT.toString())
            localStorage.setItem(CREDITS_DATE_KEY, today)
            setCredits(FREE_CREDITS_LIMIT)
            setIsExhausted(false)
        } else {
            const currentCredits = storedCredits ? parseInt(storedCredits, 10) : FREE_CREDITS_LIMIT
            setCredits(currentCredits)
            setIsExhausted(currentCredits <= 0)
        }
    }, [])

    const useCredit = (): boolean => {
        if (credits <= 0) {
            setIsExhausted(true)
            return false
        }

        const newCredits = credits - 1
        setCredits(newCredits)
        localStorage.setItem(CREDITS_STORAGE_KEY, newCredits.toString())

        if (newCredits <= 0) {
            setIsExhausted(true)
        }

        return true
    }

    return (
        <CreditsContext.Provider value={{ credits, useCredit, isExhausted }}>
            {children}
        </CreditsContext.Provider>
    )
}

export function useCredits(): CreditsContextType {
    const context = useContext(CreditsContext)
    if (context === undefined) {
        throw new Error('useCredits must be used within a CreditsProvider')
    }
    return context
}
